// HeroBanner.js
// This file contains the React component for a Hero Banner section
// and its corresponding data model for dynamic content rendering.

import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';

// Assume these are available as per the provided context (e.g., from 'BaseData.js' and 'RenderControlData.js')
// If 'String' is not explicitly imported, it usually refers to the global JavaScript String constructor.
import { RenderControlData } from './RenderControlData.js'; // Assuming this path and file exist

// Define default values for the component's props
const defaultTitle = "THE FUTURE OF FILMMAKING IS HERE";
const defaultButtonLabel = "JOIN THE WAITLIST";
const defaultButtonHref = "/waitlist";
// Placeholder for background image. In a real application, this would be a path to a default asset or a CDN URL.
const defaultBackgroundImage = "https://via.placeholder.com/1920x1080/222222/ffffff?text=Hero+Background";
const defaultOverlayColor = "rgba(0,0,0,0.5)"; // Semi-transparent dark overlay
const defaultTextColor = '#ffffff'; // White text
const defaultButtonBgColor = '#F2C94C'; // Yellow color observed in the image
const defaultButtonTextColor = '#000000'; // Black text for the button

/**
 * @typedef {object} HeroBannerProps
 * @property {string} [title="THE FUTURE OF FILMMAKING IS HERE"] - The main title text for the banner.
 * @property {string} [buttonLabel="JOIN THE WAITLIST"] - The text displayed on the call-to-action button.
 * @property {string} [buttonHref="/waitlist"] - The URL the button links to.
 * @property {string} [backgroundImage="https://via.placeholder.com/1920x1080/222222/ffffff?text=Hero+Background"] - URL of the background image.
 * @property {string} [overlayColor="rgba(0,0,0,0.5)"] - CSS color string for the overlay on the background image.
 * @property {string} [textColor="#ffffff"] - CSS color string for the title and other text.
 * @property {string} [buttonBgColor="#F2C94C"] - CSS color string for the button's background.
 * @property {string} [buttonTextColor="#000000"] - CSS color string for the button's text.
 */

/**
 * React Component for a Hero Banner section.
 * Displays a large title and a call-to-action button over a background image
 * with an optional overlay, mimicking the provided image.
 *
 * @param {HeroBannerProps} props - The properties for the HeroBanner component.
 */
const HeroBanner = ({
  title = defaultTitle,
  buttonLabel = defaultButtonLabel,
  buttonHref = defaultButtonHref,
  backgroundImage = defaultBackgroundImage,
  overlayColor = defaultOverlayColor,
  textColor = defaultTextColor,
  buttonBgColor = defaultButtonBgColor,
  buttonTextColor = defaultButtonTextColor,
}) => {
  // Styles for the main section container
  const sectionStyle = {
    position: 'relative',
    height: '80vh', // Make the banner take up 80% of the viewport height for prominence
    display: 'flex',
    alignItems: 'center', // Vertically center content
    justifyContent: 'center', // Horizontally center content
    overflow: 'hidden', // Ensures background image/overlay don't spill
  };

  // Styles for the background image
  const backgroundStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover', // Cover the entire area without distortion
    backgroundPosition: 'center', // Center the image within the area
    zIndex: 0, // Behind overlay and content
  };

  // Styles for the semi-transparent overlay
  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: overlayColor, // Configurable overlay color and opacity
    zIndex: 1, // Above background, below content
  };

  // Styles for the content (title and button)
  const contentStyle = {
    position: 'relative',
    zIndex: 2, // Above overlay
    textAlign: 'center', // Center text and button
    color: textColor, // Configurable text color
    padding: '1rem',
  };

  // Styles for the main title
  const titleStyle = {
    fontSize: 'clamp(2.5rem, 6vw, 5rem)', // Responsive font size for large impact
    fontWeight: 'bold', // Bold font as seen in the image
    lineHeight: '1.1', // Tighter line height for multi-line titles
    marginBottom: '1.5rem',
    textShadow: '2px 2px 4px rgba(0,0,0,0.7)', // Add shadow for readability over busy backgrounds
  };

  // Styles for the call-to-action button
  const buttonCustomStyle = {
    backgroundColor: buttonBgColor, // Configurable button background
    color: buttonTextColor, // Configurable button text color
    borderColor: buttonBgColor, // Border matches background for consistency
    padding: '0.8rem 2.5rem', // Generous padding for a large button
    fontSize: '1.2rem',
    fontWeight: 'bold',
    borderRadius: '0.3rem', // Slight rounded corners for the button
  };

  return (
    <section style={sectionStyle}>
      {/* Background image container */}
      <div style={backgroundStyle} aria-hidden="true" />
      {/* Overlay container for tinting */}
      <div style={overlayStyle} aria-hidden="true" />

      {/* Main content container, centered within the section */}
      <Container fluid className="h-100 d-flex align-items-center justify-content-center">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <div style={contentStyle}>
              <h1 style={titleStyle}>{title}</h1>
              <Button href={buttonHref} style={buttonCustomStyle} className="mt-3">
                {buttonLabel}
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

/**
 * Data model for the Hero Banner section.
 * Extends RenderControlData to fit into the dynamic rendering system,
 * providing schema validation capabilities.
 */
class HeroBannerData extends RenderControlData {
  // Define the expected properties and their types for schema validation.
  // Assuming 'String' refers to the global JavaScript String constructor for type annotation,
  // following the pattern seen in the provided examples.
  static annotations = Object.assign({}, RenderControlData.annotations, {
    title: String,
    buttonLabel: String,
    buttonHref: String,
    backgroundImage: String,
    overlayColor: String,
    textColor: String,
    buttonBgColor: String,
    buttonTextColor: String,
  });

  /**
   * Constructs a new HeroBannerData instance.
   * Initializes properties with data from the JSON object.
   * @param {object} data - The raw data object from the JSON.
   */
  constructor(data = {}) {
    super(data); // Call the parent constructor if RenderControlData has one
    Object.assign(this, data); // Assign properties from the data object to this instance
  }
}

// Export the HeroBanner component and its HeroBannerData class.
// This allows other modules (like the main rendering binder) to import and use them.
export { HeroBanner, HeroBannerData };
export default HeroBanner;


/**
 * LLM EXAMPLE JSON (HeroBanner)
 *
 * This JSON represents a typical configuration for the HeroBanner component.
 * It would be included within the 'sections' array of a page-level JSON file,
 * or as a standalone content definition.
 *
 * {
 *   "render_control_type": "hero_banner",
 *   "title": "THE FUTURE OF FILMMAKING IS HERE",
 *   "buttonLabel": "JOIN THE WAITLIST",
 *   "buttonHref": "/join-waitlist",
 *   "backgroundImage": "/images/eye_background.webp",
 *   "overlayColor": "rgba(0,0,0,0.6)",
 *   "textColor": "#ffffff",
 *   "buttonBgColor": "#F2C94C",
 *   "buttonTextColor": "#000000"
 * }
 *
 * Notes for Integration:
 * - "render_control_type": This field is crucial. It must be "hero_banner"
 *   so that the main rendering logic (e.g., ArtLab.jsx or a ContentBinder component)
 *   can correctly identify and route to the HeroBanner React component.
 * - "title": The main heading displayed prominently on the banner.
 * - "buttonLabel": The text shown on the call-to-action button.
 * - "buttonHref": The URL that the button will navigate to when clicked.
 * - "backgroundImage": Specifies the URL of the image to be used as the background.
 *   This can be a local path or an external URL. In the provided image, it's a close-up of an eye.
 * - "overlayColor": A CSS color string (e.g., "rgba(0,0,0,0.6)" for a 60% opaque black overlay)
 *   to apply a tint over the background image. This helps improve text readability, especially
 *   over busy or light backgrounds.
 * - "textColor": The CSS color string for the main title and any other text within the banner.
 * - "buttonBgColor": The CSS color string for the background of the call-to-action button.
 * - "buttonTextColor": The CSS color string for the text on the call-to-action button.
 * - Default Values: If any of the optional properties (like colors or image paths) are omitted
 *   from the JSON, the HeroBanner component will fall back to its internal default values
 *   to ensure a consistent look.
 *
 * To integrate this component:
 * 1. Ensure 'HeroBanner.js' is placed in your components directory (e.g., 'components/HeroBanner.js').
 * 2. In your main rendering binder component (e.g., 'ArtLab.jsx' or 'ContentBinder.jsx'):
 *    a. Import the component: `import { HeroBanner } from './components/HeroBanner';`
 *    b. Add a condition in your rendering loop (e.g., `sections.map(...)`):
 *       ```jsx
 *       if (section.render_control_type === 'hero_banner') {
 *         return (
 *           <HeroBanner
 *             key={i} // Remember to use a unique key for list rendering
 *             title={section.title}
 *             buttonLabel={section.buttonLabel}
 *             buttonHref={section.buttonHref}
 *             backgroundImage={section.backgroundImage}
 *             overlayColor={section.overlayColor}
 *             textColor={section.textColor}
 *             buttonBgColor={section.buttonBgColor}
 *             buttonTextColor={section.buttonTextColor}
 *           />
 *         );
 *       }
 *       ```
 * 3. In the file defining your data models (e.g., 'data_models.js' or 'RenderControlData.js'):
 *    a. Import `HeroBannerData`: `import { HeroBannerData } from './components/HeroBanner';`
 *    b. Update your `mkSectionInstance` function to include a case for "hero_banner":
 *       ```jsx
 *       if (s.render_control_type === 'hero_banner') return new HeroBannerData(s);
 *       ```
 */